/**
 * Pure helpers for VersionHistoryDrawer session diffs (Phase 4.5 tests).
 */

import type { AssessmentType } from './assessment-versions';

export const VERSION_DIFF_FIELD_LABELS: Record<string, string> = {
  score: 'Score',
  analysis: 'Analysis',
  goals: 'Goals / Action Plan',
  evidence: 'Evidence',
};

export interface VersionDiffEntry {
  field: string;
  label: string;
  oldVal: unknown;
  newVal: unknown;
}

export interface SessionVersionDiffEntry extends VersionDiffEntry {
  pilotRole: string;
  competency: string;
}

export function normalizeForDiff(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeForDiff);
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
      a.localeCompare(b),
    );
    const normalized: Record<string, unknown> = {};
    entries.forEach(([key, child]) => {
      normalized[key] = normalizeForDiff(child);
    });
    return normalized;
  }
  return value;
}

export function valuesAreEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(normalizeForDiff(a)) === JSON.stringify(normalizeForDiff(b));
}

/** Read score/analysis/goals/evidence from a per-row or composite criterion snapshot. */
export function extractVersionDiffFields(
  snapshot: Record<string, unknown>,
): Record<string, unknown> {
  return {
    score: snapshot.score,
    analysis: snapshot.analysis,
    goals: snapshot.goals,
    evidence: snapshot.evidence,
  };
}

export function getSessionVersionDiffs(
  snapA: Record<string, unknown>,
  snapB: Record<string, unknown>,
  assessmentType: 'pilot' | 'instructor',
): SessionVersionDiffEntry[] {
  const result: SessionVersionDiffEntry[] = [];

  if (assessmentType === 'instructor') {
    const allCriteria = new Set([...Object.keys(snapA), ...Object.keys(snapB)]);
    for (const criterion of allCriteria) {
      const compA = extractVersionDiffFields(
        (snapA[criterion] ?? {}) as Record<string, unknown>,
      );
      const compB = extractVersionDiffFields(
        (snapB[criterion] ?? {}) as Record<string, unknown>,
      );
      for (const field of ['score', 'analysis', 'goals', 'evidence'] as const) {
        if (!valuesAreEqual(compA[field], compB[field])) {
          result.push({
            pilotRole: criterion,
            competency: criterion,
            field,
            label: VERSION_DIFF_FIELD_LABELS[field] ?? field,
            oldVal: compA[field],
            newVal: compB[field],
          });
        }
      }
    }
    return result;
  }

  const allRoles = new Set([...Object.keys(snapA), ...Object.keys(snapB)]);
  for (const pilotRole of allRoles) {
    const roleA = (snapA[pilotRole] ?? {}) as Record<string, unknown>;
    const roleB = (snapB[pilotRole] ?? {}) as Record<string, unknown>;
    const allComps = new Set([...Object.keys(roleA), ...Object.keys(roleB)]);

    for (const competency of allComps) {
      const compA = extractVersionDiffFields((roleA[competency] ?? {}) as Record<string, unknown>);
      const compB = extractVersionDiffFields((roleB[competency] ?? {}) as Record<string, unknown>);

      for (const field of ['score', 'analysis', 'goals', 'evidence'] as const) {
        if (!valuesAreEqual(compA[field], compB[field])) {
          result.push({
            pilotRole,
            competency,
            field,
            label: VERSION_DIFF_FIELD_LABELS[field] ?? field,
            oldVal: compA[field],
            newVal: compB[field],
          });
        }
      }
    }
  }
  return result;
}

export type { AssessmentType };
